/**
 * Proscenium + ceremony art registry — the batch-1/3 ornament assets, imported so
 * Vite hashes them and StageView/ProsceniumFrame/reveal screens reference stable
 * URLs. Kept in one module so a re-cut asset drops in at the same path with zero
 * call-site change (the Store-manifest philosophy).
 *
 * Blend-mode notes (load-bearing):
 *  - spotlight cones + moon disc are PURE BLACK OPAQUE art authored
 *    for `mix-blend-mode: screen` — under screen, black reads as transparent and
 *    only the light lifts. Never render them `normal` (you'd get a black box).
 *  - grain tile is a ~5% overlay.
 */

// ── Proscenium chrome ─────────────────────────────────────────────────────────
import filigreeCorner from '../assets/ornaments/filigree-corner.webp';
import grainTile from '../assets/ornaments/grain-tile.webp';
import goldDivider from '../assets/ornaments/gold-divider.webp';

// ── Stage lighting (screen-blend, black = transparent) ────────────────────────
import spotlightCone from '../assets/ornaments/spotlight-cone.webp';
import spotlightConeWide from '../assets/ornaments/spotlight-cone-wide.webp';

// ── Ceremony set pieces ───────────────────────────────────────────────────────
import moonDisc from '../assets/ceremony/moon-disc.webp';
// Alpha-keyed variant of moon-disc: the pure-black background is pre-baked to
// transparency (disc kept opaque, corona feathered) so the moon composites with a
// normal `over` blend and NEVER depends on mix-blend-mode:screen reaching the scene
// through a stacking context. Use this instead of `moonDisc` + screen-blend.
import moonDiscKeyed from '../assets/ceremony/moon-disc-keyed.webp';
// The plaque webp textures (plaque-clean/-cracked/-dead) were removed as plate
// backers 2026-07-11 — the art carries its own baked ornate frame + wood-plank
// composition, so any crop behind a variable-width plate bled plank smears and
// frame fragments mid-plate. Plaques are now CSS-only (stage/components/Plaque.tsx).
// Files stay on disk; the barrel keys are gone (no consumers). claw-gouge stays.
import clawGouge from '../assets/ceremony/claw-gouge.webp';
import wolfRidge from '../assets/ceremony/wolf-ridge.webp';

// ── Seals ─────────────────────────────────────────────────────────────────────
import waxSeal from '../assets/ornaments/wax-seal.webp';
import waxSealCracked from '../assets/ornaments/wax-seal-cracked.webp';

export const ornaments = {
  filigreeCorner,
  grainTile,
  goldDivider,
  spotlightCone,
  spotlightConeWide,
  waxSeal,
  waxSealCracked,
} as const;

export const ceremony = {
  moonDisc,
  moonDiscKeyed,
  clawGouge,
  wolfRidge,
} as const;
